import { useStore } from '@/store'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    aplicarVisibilidade,
    extrairVisibilidade,
    podarColunasMortas,
} from '../utils/columnVisibilityStorage'
import { GridColDefPro } from '../utils/types'

/**
 * Estado das colunas do DataTable, persistido no localStorage quando `tableId`
 * é informado. Sem `tableId`, funciona como um `useState` comum — nada é lido
 * nem gravado no store.
 *
 * `tableId` precisa ser estável durante a vida do componente: trocar o id num
 * componente montado gravaria dados de uma tabela sob a chave de outra — se o
 * id precisar variar, remonte o grid com `key`.
 */
export default function useColunasPersistidas(
    tableId: string | undefined,
    columns: GridColDefPro[],
) {
    const salvo = useStore(state =>
        tableId ? state.dataTable.colunas[tableId] : undefined,
    )
    const persistirColunas = useStore(state => state.dataTable.setColunas)

    /**
     * Snapshot dos defaults, tirado no primeiro render.
     * O memo `cols` do useDataGridPro muta `column.hide` quando há
     * `columnVisibilityModel`, e em várias páginas `columns` é constante de
     * módulo — sem a cópia, o padrão do reset viria corrompido.
     */
    const defaultsRef = useRef(columns.map(column => ({ ...column })))

    const [colunas, setColunasState] = useState<GridColDefPro[]>(() =>
        salvo
            ? aplicarVisibilidade(defaultsRef.current, salvo)
            : defaultsRef.current,
    )
    const colunasRef = useRef(colunas)

    // Poda uma vez por tabela: campos que não existem mais saem do store.
    useEffect(() => {
        if (!tableId || !salvo) return

        const podado = podarColunasMortas(salvo, defaultsRef.current)
        // podarColunasMortas só remove chaves, então comparar o tamanho basta.
        if (Object.keys(podado).length !== Object.keys(salvo).length)
            persistirColunas(tableId, podado)
    }, [tableId])

    const setColunas = useCallback(
        (updater: React.SetStateAction<GridColDefPro[]>) => {
            const next =
                typeof updater === 'function'
                    ? updater(colunasRef.current)
                    : updater

            colunasRef.current = next
            setColunasState(next)

            // Fora do updater do setState de propósito: em StrictMode o updater
            // roda duas vezes e gravaria em duplicidade.
            if (tableId) persistirColunas(tableId, extrairVisibilidade(next))
        },
        [tableId, persistirColunas],
    )

    /** Volta aos defaults do colDef e apaga o registro persistido da tabela. */
    const resetColunas = useCallback(() => {
        colunasRef.current = defaultsRef.current
        setColunasState(defaultsRef.current)

        if (tableId) persistirColunas(tableId)
    }, [tableId, persistirColunas])

    /** Sem `tableId` não há estado persistido: as colunas acompanham a prop. */
    useEffect(() => {
        if (tableId) return
        colunasRef.current = columns
        setColunasState(columns)
    }, [columns, tableId])

    /**
     * Com `tableId` a prop `columns` é ignorada enquanto a lista de campos não
     * muda: o layout salvo do usuário vence os defaults da página. Quando ela
     * muda — páginas que derivam colunas de outro estado, como o Ponto Diário e
     * os níveis de agrupamento — os campos novos entram com o default do colDef
     * e os que continuam mantêm a escolha do usuário.
     */
    const camposAtuais = columns.map(column => column.field).join('|')
    const camposRef = useRef(camposAtuais)

    useEffect(() => {
        if (!tableId || camposRef.current === camposAtuais) return
        camposRef.current = camposAtuais

        const novosDefaults = columns.map(column => ({ ...column }))
        defaultsRef.current = novosDefaults

        const proximo = aplicarVisibilidade(
            novosDefaults,
            extrairVisibilidade(colunasRef.current),
        )

        colunasRef.current = proximo
        setColunasState(proximo)
    }, [camposAtuais, tableId])

    return { colunas, setColunas, resetColunas }
}
